import * as React from 'react';
import ModalSelecionar, { BuscarOpcoes, OpcaoAuto, buscarPaginado } from './ModalSelecionar';
import { IconeLupa } from './CaixaBusca';

export type { OpcaoAuto, BuscarOpcoes } from './ModalSelecionar';
export { buscarPaginado } from './ModalSelecionar';

/** CR (centro de resultado): usado no cadastro de usuario e na edicao de
    dispositivo, com o mesmo endpoint e ordenacao que os dois ja usavam. */
export const buscarCrs = buscarPaginado(
  (termo, pagina, linhas) =>
    `/crs/paginado?page=${pagina}&rows=${linhas}&campo=descricaoCompleta&valor=${encodeURIComponent(termo)}` +
    `&direcao=asc&ordenar=descricaoCompleta`,
  (c) => ({ codigo: c.codigo, rotulo: c.descricaoCompleta || c.descricao || '—', secundario: String(c.codigo ?? ''), dado: c })
);

/* ============================================================================
   Campos de escolher UM ou VARIOS itens de uma lista grande.

   DE AUTOCOMPLETE PARA LUPA + MODAL (21/10/2026, a pedido).
   Ate aqui o campo abria uma lista embaixo e cada chamador carregava as opcoes
   ao abrir o formulario (rows=500, rows=9999) e de novo a cada pausa na
   digitacao. O pedido foi "do jeito que esta esta consumindo".

   Agora o campo nao consulta nada sozinho. Ele mostra o que ja foi escolhido
   em fichas (removiveis no "x") e tem uma LUPA: clicar nela, ou Enter no campo,
   abre o ModalSelecionar com o texto digitado ja como filtro, paginado no
   servidor. Quem chama passa `buscar` (normalmente `buscarPaginado` com o mesmo
   endpoint e os mesmos filtros de antes) em vez de `opcoes`/`aoBuscar`.

   As fichas continuam ACIMA do campo pelo mesmo motivo de antes: o que esta
   escolhido fica visivel sem abrir nada. E Backspace no campo vazio remove a
   ultima ficha - o reflexo de quem acabou de escolher errado.

   ATENCAO ao usar dentro de `Campo`: passe `bloco`. Com <label>, clicar em
   qualquer canto do campo acionaria o primeiro botao (o "x" da primeira ficha) -
   ver o comentario de `bloco` em Modal.tsx.
   ============================================================================ */

/** Caixa com lupa que so ABRE a selecao (Enter ou clique) - nao consulta nada. */
export function CampoLupa({
  placeholder,
  aoAbrir,
  aoApagarVazio,
}: {
  placeholder: string;
  aoAbrir: (termo: string) => void;
  aoApagarVazio?: () => void;
}) {
  const [texto, setTexto] = React.useState('');
  const abrir = () => {
    aoAbrir(texto);
    setTexto('');
  };
  return (
    <div className="flex h-9 items-center rounded-lg border border-line bg-surface focus-within:border-primary">
      <input
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            // Enter aqui e "procurar", nao "salvar o formulario".
            e.preventDefault();
            e.stopPropagation();
            abrir();
          } else if (e.key === 'Backspace' && !texto && aoApagarVazio) {
            e.preventDefault();
            aoApagarVazio();
          }
        }}
        placeholder={placeholder}
        className="h-full min-w-0 flex-1 bg-transparent px-3 text-[12.5px] text-ink outline-none placeholder:text-faint"
      />
      <button
        type="button"
        onClick={abrir}
        aria-label="Pesquisar e selecionar"
        title="Pesquisar e selecionar"
        className="grid h-full w-9 flex-none place-items-center rounded-r-lg border-l border-line text-muted-foreground hover:bg-surface-2 hover:text-primary"
      >
        <IconeLupa />
      </button>
    </div>
  );
}

/* ============================================================================
   VARIOS itens.
   ============================================================================ */
export function AutoCompleteMultiplo({
  selecionados,
  aoMudar,
  buscar,
  titulo = 'Selecionar',
  maximo,
  placeholder = 'Digite e clique na lupa',
  vazio = 'Nada encontrado.',
}: {
  selecionados: OpcaoAuto[];
  /** Lista nova inteira - no "Confirmar" do modal ou ao remover uma ficha. */
  aoMudar: (lista: OpcaoAuto[]) => void;
  buscar: BuscarOpcoes;
  titulo?: string;
  maximo?: number;
  placeholder?: string;
  vazio?: string;
}) {
  const [termo, setTermo] = React.useState<string | null>(null);
  const remover = (o: OpcaoAuto) => aoMudar(selecionados.filter((s) => String(s.codigo) !== String(o.codigo)));

  return (
    <div>
      {!!selecionados.length && (
        <div className="mb-1.5 flex flex-wrap gap-1.5">
          {selecionados.map((s) => (
            <span
              key={s.codigo}
              className="inline-flex h-7 max-w-[240px] items-center gap-1.5 rounded-lg border border-primary bg-primary/10 pl-2.5 pr-1 text-[12px] font-semibold text-ink"
            >
              <span className="truncate">{s.rotulo}</span>
              <button
                type="button"
                onClick={() => remover(s)}
                aria-label={`Remover ${s.rotulo}`}
                title="Remover"
                className="grid h-5 w-5 flex-none place-items-center rounded text-[12.5px] leading-none text-muted-foreground hover:bg-surface hover:text-danger"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <CampoLupa
        placeholder={placeholder}
        aoAbrir={setTermo}
        aoApagarVazio={selecionados.length ? () => remover(selecionados[selecionados.length - 1]) : undefined}
      />

      {termo !== null && (
        <ModalSelecionar
          multiplo
          titulo={titulo}
          buscar={buscar}
          inicial={selecionados}
          termoInicial={termo}
          maximo={maximo}
          vazio={vazio}
          aoConfirmar={aoMudar}
          onFechar={() => setTermo(null)}
        />
      )}
    </div>
  );
}

/* ============================================================================
   UM item.
   ============================================================================ */
export function AutoCompleteUnico({
  selecionado,
  aoEscolher,
  buscar,
  titulo = 'Selecionar',
  placeholder = 'Digite e clique na lupa',
  vazio = 'Nada encontrado.',
}: {
  selecionado: OpcaoAuto | null;
  aoEscolher: (o: OpcaoAuto | null) => void;
  buscar: BuscarOpcoes;
  titulo?: string;
  placeholder?: string;
  vazio?: string;
}) {
  const [termo, setTermo] = React.useState<string | null>(null);

  const modal = termo !== null && (
    <ModalSelecionar
      titulo={titulo}
      buscar={buscar}
      inicial={selecionado ? [selecionado] : []}
      termoInicial={termo}
      vazio={vazio}
      // Confirmar sem nada marcado nao apaga a escolha: para isso existe "remover".
      aoConfirmar={(l) => l[0] && aoEscolher(l[0])}
      onFechar={() => setTermo(null)}
    />
  );

  /* Com item escolhido, o campo sai da frente e entra a ficha do escolhido. Um
     campo de busca com valor escolhido ao lado gera a duvida "o que vale, o que
     digitei ou o que esta marcado?". */
  if (selecionado) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-lg border border-primary bg-primary/10 p-2.5">
        <span className="min-w-0">
          <span className="block truncate text-[12px] font-semibold">{selecionado.rotulo}</span>
          {selecionado.secundario && (
            <span className="block truncate text-[11px] text-muted-foreground">{selecionado.secundario}</span>
          )}
        </span>

        <span className="flex flex-none items-center gap-2">
          <button type="button" onClick={() => setTermo('')} className="text-[11px] font-semibold text-primary">
            trocar
          </button>
          <button type="button" onClick={() => aoEscolher(null)} className="text-[11px] font-semibold text-danger">
            remover
          </button>
        </span>
        {modal}
      </div>
    );
  }

  return (
    <div>
      <CampoLupa placeholder={placeholder} aoAbrir={setTermo} />
      {modal}
    </div>
  );
}
